import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Server, RefreshCw, Loader2, Info, Globe } from 'lucide-react';

export default function PowerDnsPortal() {
  const [status, setStatus] = useState(null);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusRes, zonesRes] = await Promise.allSettled([
        api.get('/powerdns/status'),
        api.get('/powerdns/zones'),
      ]);
      if (statusRes.status === 'fulfilled') setStatus(statusRes.value.data);
      if (zonesRes.status === 'fulfilled') setZones(zonesRes.value.data.zones || []);
    } catch { /* keep honest empty state */ }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const configured = status?.configured;
  const connected = status?.connected;

  return (
    <div className="co-scope co-settings min-h-screen bg-[#041019] p-6 sm:p-8" data-testid="powerdns-portal-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-[#0e3a4a] flex items-center justify-center">
              <Server className="h-6 w-6 text-[#37c9ee]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="powerdns-title">PowerDNS</h1>
              <p className="text-sm text-gray-400">Authoritative DNS server — self-hosted</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            data-testid="powerdns-refresh-btn"
            className="border-[#193b4b] text-gray-200 hover:bg-[#0e2a36]"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>

        {/* Separation note: this is NOT eNom */}
        <div data-testid="powerdns-separation-note" className="flex items-start gap-2 rounded-md border border-[#193b4b] bg-[#081a25] px-4 py-3 text-sm text-gray-400">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-gray-500" />
          <span>
            PowerDNS manages DNS <strong className="text-gray-200">zones and records</strong> on your own authoritative name server.
            It is separate from the <span className="inline-flex items-center gap-1 text-gray-200"><Globe className="h-3.5 w-3.5" />eNom</span> domain
            <strong className="text-gray-200"> registrar</strong> integration, which handles domain purchases, transfers and WHOIS.
          </span>
        </div>

        {/* Status card */}
        <Card className="bg-[#081a25] border-[#193b4b]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-lg">Connection</CardTitle>
              {loading ? (
                <Badge className="bg-gray-600 text-white" data-testid="powerdns-status-badge">Checking…</Badge>
              ) : configured && connected ? (
                <Badge className="bg-green-600 text-white" data-testid="powerdns-status-badge">Connected</Badge>
              ) : configured ? (
                <Badge className="bg-amber-600 text-white" data-testid="powerdns-status-badge">Configured · unreachable</Badge>
              ) : (
                <Badge className="bg-gray-500 text-white" data-testid="powerdns-status-badge">Not configured</Badge>
              )}
            </div>
            <CardDescription className="text-gray-400">
              {loading
                ? 'Checking PowerDNS connection…'
                : (status?.message || (connected ? `Connected to ${status?.api_url || 'PowerDNS'}` : 'PowerDNS connection unavailable.'))}
            </CardDescription>
          </CardHeader>
          {!loading && !configured && (
            <CardContent>
              <div data-testid="powerdns-unconfigured-help" className="rounded-md border border-dashed border-[#2a4a5a] bg-[#0b2029] p-4 text-sm text-gray-300 space-y-2">
                <p className="font-medium text-white">PowerDNS isn't set up yet.</p>
                <p className="text-gray-400">
                  To connect your authoritative DNS server, set the following in the backend environment, then refresh:
                </p>
                <ul className="list-disc pl-5 text-gray-400 space-y-1">
                  <li><code className="text-[#37c9ee]">POWERDNS_API_URL</code> — e.g. <code>http://your-dns-host:8081</code></li>
                  <li><code className="text-[#37c9ee]">POWERDNS_API_KEY</code> — the API key from your PowerDNS config</li>
                  <li><code className="text-[#37c9ee]">POWERDNS_SERVER_ID</code> — optional, defaults to <code>localhost</code></li>
                </ul>
                <p className="text-xs text-gray-500">No zones or records are shown until a real server responds — nothing here is simulated.</p>
              </div>
            </CardContent>
          )}
          {!loading && configured && connected && (
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div><div className="text-gray-500">Server</div><div className="text-gray-200">{status?.server_id || '—'}</div></div>
                <div><div className="text-gray-500">Version</div><div className="text-gray-200">{status?.version || '—'}</div></div>
                <div><div className="text-gray-500">Daemon</div><div className="text-gray-200">{status?.daemon_type || '—'}</div></div>
                <div><div className="text-gray-500">API</div><div className="text-gray-200 truncate">{status?.api_url || '—'}</div></div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Zones */}
        <Card className="bg-[#081a25] border-[#193b4b]">
          <CardHeader>
            <CardTitle className="text-white text-lg">Zones</CardTitle>
            <CardDescription className="text-gray-400">
              Authoritative DNS zones on this server (read-only).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {zones.length === 0 ? (
              <div data-testid="powerdns-zones-empty" className="py-8 text-center text-gray-500 text-sm">
                {configured ? 'No zones found on this PowerDNS server.' : 'Connect PowerDNS to list your DNS zones.'}
              </div>
            ) : (
              <Table data-testid="powerdns-zones-table">
                <TableHeader>
                  <TableRow className="border-[#193b4b]">
                    <TableHead className="text-gray-400">Zone</TableHead>
                    <TableHead className="text-gray-400">Kind</TableHead>
                    <TableHead className="text-gray-400">Serial</TableHead>
                    <TableHead className="text-gray-400">DNSSEC</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {zones.map((z) => (
                    <TableRow key={z.id || z.name} className="border-[#193b4b]" data-testid="powerdns-zone-row">
                      <TableCell className="text-gray-200 font-medium">{z.name}</TableCell>
                      <TableCell className="text-gray-300">{z.kind || '—'}</TableCell>
                      <TableCell className="text-gray-300">{z.serial ?? '—'}</TableCell>
                      <TableCell className="text-gray-300">{z.dnssec ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
